/* @jsx h */
import { defineComponent, h, onMounted, onBeforeUnmount, ref } from "vue";
import { RouterView } from "vue-router";
import { PageComponent } from "@lemmonhq/uikit";
import { setTitle } from "@lemmonhq/orders";
import { useTrpc } from "@app/vue/providers/TrpcProvider";

import { formatAdminPrice } from "./helpers";
import type { AdminShopEvent } from "@lemmonhq/types";
import { useToast } from "@app/vue/providers/Toast";

export { AdminDashboardPage } from "./subpages/Dashboard";
export { AdminRestaurantsPage } from "./subpages/Restaurants";
export { AdminSettingsPage } from "./subpages/Settings";

export const AdminLayoutPage = defineComponent({
  name: "AdminLayoutPage",
  setup() {
    const trpc = useTrpc();
    const { showToast } = useToast();
    const lastTimestamp = ref<string>(new Date().toISOString());
    const restaurantsMap = ref<Map<number, string>>(new Map());
    const eventQueue = ref<AdminShopEvent[]>([]);
    const isProcessingQueue = ref(false);
    let updateInterval: number | null = null;
    setTitle("Admin");
    const fetchRestaurants = async () => {
      try {
        const restaurants = await trpc.admin.restaurantOptions.query();
        const map = new Map<number, string>();
        restaurants.forEach((r) => {
          map.set(r.id, r.name);
        });
        restaurantsMap.value = map;
      } catch (error) {
        console.error("Failed to fetch restaurants:", error);
      }
    };

    const processEventQueue = async () => {
      if (isProcessingQueue.value || eventQueue.value.length === 0) {
        return;
      }

      isProcessingQueue.value = true;

      while (eventQueue.value.length > 0) {
        const event = eventQueue.value.shift();
        if (!event) break;

        showToastForEvent(event);
        await new Promise((resolve) => setTimeout(resolve, 9000));
      }

      isProcessingQueue.value = false;
    };

    const showToastForEvent = (event: AdminShopEvent) => {
      const restaurantName =
        restaurantsMap.value.get(event.shop_id) || "Unknown";
      const payload = event.payload || {};

      if (event.name === "order_created") {
        const amount = payload.subtotal || 0;
        const serviceMethod: string = payload.service_method || "Unknown";
        const isTableOrder = payload.table_id != null;
        const orderType = isTableOrder
          ? "Table Order"
          : serviceMethod.toLocaleUpperCase();

        showToast(
          `${restaurantName} • ${orderType} • ${formatAdminPrice(
            amount,
            true,
          )}`,
          {
            type: "success",
            icon: "receipt_long",
            title: "Order Created",
            autoHide: 6000,
          },
        );
      } else if (event.name === "payment_created" && payload.table_id) {
        const amount = payload.amount || 0;
        showToast(
          `${restaurantName} • ${(
            payload.method as string
          ).toLocaleUpperCase()}  • ${formatAdminPrice(amount, true)}`,
          {
            type: "info",
            icon: "payments",
            title: "Payment Created",
            autoHide: 6000,
          },
        );
      }
    };

    const fetchUpdates = async () => {
      try {
        const events = await trpc.admin.getUpdates.query({
          sinceTimestamp: lastTimestamp.value,
        });

        // Always update timestamp to prevent querying the same range
        if (events.length > 0) {
          // Update timestamp to max of all events using Date comparison
          const maxTimestamp = events.reduce((max, event) => {
            const eventTime = new Date(event.created_at).getTime();
            const maxTime = new Date(max).getTime();
            return eventTime > maxTime ? event.created_at : max;
          }, lastTimestamp.value);
          lastTimestamp.value = maxTimestamp;

          // Filter events to only include order_created and payment_created with table_id
          const relevantEvents = events.filter((event) => {
            if (event.name === "order_created") {
              return true;
            }
            if (event.name === "payment_created") {
              const payload = event.payload || {};
              return payload.table_id != null;
            }
            return false;
          });

          // Add to queue
          if (relevantEvents.length > 0) {
            eventQueue.value.push(...relevantEvents);
            // Start processing queue
            processEventQueue();
          }
        } else {
          // No events, update timestamp to current time to advance the window
          lastTimestamp.value = new Date().toISOString();
        }
      } catch (error) {
        console.error("Failed to fetch updates:", error);
      }
    };

    onMounted(() => {
      setTitle("Admin");
      fetchRestaurants();
      fetchUpdates();

      updateInterval = window.setInterval(() => {
        fetchUpdates();
      }, 30000);
    });

    onBeforeUnmount(() => {
      if (updateInterval) {
        clearInterval(updateInterval);
      }
    });

    const subpages = [
      {
        name: "Dashboard",
        icon: "dashboard",
        href: "/app/admin/dashboard",
      },
      {
        name: "Restaurants",
        icon: "storefront",
        href: "/app/admin/restaurants",
      },
      {
        name: "Settings",
        icon: "settings",
        href: "/app/admin/settings",
      },
    ];

    return () => (
      <PageComponent
        title="Admin"
        titleIcon="shield_person"
        subpages={subpages}
      >
        <RouterView />
      </PageComponent>
    );
  },
});
